import type { ActiveRemedy, DetectedDebt, PlanetEffect } from "@/lib/api/lal-kitab";

import { RESULT_COPY, type ResultLocale } from "./LalKitabKundliResults.copy";

function EffectBadge({ effect, language }: { effect: "good" | "bad"; language: ResultLocale }) {
  const copy = RESULT_COPY[language];
  const stateClass =
    effect === "good"
      ? "bg-intent-success-surface text-intent-success"
      : "bg-intent-error-surface text-intent-error";
  return (
    <span className={`rounded-full px-2 py-0.5 text-caption font-semibold ${stateClass}`}>
      {effect === "good" ? copy.comfortable : copy.needsUpay}
    </span>
  );
}

export function PlanetEffectsTable({
  effects,
  language,
}: {
  effects: PlanetEffect[];
  language: ResultLocale;
}) {
  const copy = RESULT_COPY[language];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-body-small">
        <thead>
          <tr className="border-b border-border-default">
            <th className="py-2 pr-4 text-left font-semibold text-text-secondary">{copy.planet}</th>
            <th className="py-2 pr-4 text-center font-semibold text-text-secondary">
              {copy.house}
            </th>
            <th className="py-2 text-left font-semibold text-text-secondary">{copy.effect}</th>
          </tr>
        </thead>
        <tbody>
          {effects.map((effect) => (
            <tr key={effect.planet} className="border-b border-border-subtle">
              <td className="py-2 pr-4 text-text-primary">
                <span className="font-medium">
                  {language === "hi" ? effect.planet_display_hi : effect.planet_display_en}
                </span>
                {language === "en" ? (
                  <span className="ml-2 text-caption text-text-muted">
                    {effect.planet_display_hi}
                  </span>
                ) : null}
              </td>
              <td className="py-2 pr-4 text-center font-semibold text-intent-brand">
                {effect.house}
              </td>
              <td className="py-2">
                <EffectBadge effect={effect.effect as "good" | "bad"} language={language} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RemedyCard({ remedy, language }: { remedy: ActiveRemedy; language: ResultLocale }) {
  const copy = RESULT_COPY[language];
  return (
    <article className="rounded-xl border border-border-default bg-surface-raised p-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-body font-semibold text-text-primary">
          {language === "hi" ? remedy.planet_display_hi : remedy.planet_display_en}
        </h3>
        <span className="text-caption text-text-muted">
          {copy.house} {remedy.house} · {remedy.day}
        </span>
      </div>
      {remedy.remedy_summary ? (
        <p className="mb-2 text-body-small text-text-secondary">{remedy.remedy_summary}</p>
      ) : null}
      {remedy.donate.length > 0 ? (
        <RemedyLine label={copy.donate} value={remedy.donate.join(", ")} />
      ) : null}
      {remedy.feed ? <RemedyLine label={copy.feed} value={remedy.feed} /> : null}
      {remedy.gemstone ? <RemedyLine label={copy.gemstone} value={remedy.gemstone} /> : null}
    </article>
  );
}

function RemedyLine({ label, value }: { label: string; value: string }) {
  return (
    <p className="mt-1 text-caption text-text-secondary">
      <span className="font-semibold text-intent-brand">{label}: </span>
      {value}
    </p>
  );
}

export function DebtCard({ debt, language }: { debt: DetectedDebt; language: ResultLocale }) {
  const copy = RESULT_COPY[language];
  return (
    <article className="rounded-xl border border-l-4 border-border-default border-l-intent-brand bg-surface-raised p-4">
      <h3 className="mb-1 text-body-small font-semibold text-text-primary">{debt.name}</h3>
      <p className="mb-2 text-caption text-text-muted">
        {language === "hi" ? debt.planet_display_hi : debt.planet_display_en} · {copy.house}{" "}
        {debt.house}
      </p>
      <p className="text-body-small text-text-secondary">
        <span className="font-semibold">{copy.remedy}: </span>
        {debt.remedy}
      </p>
    </article>
  );
}
